import { useFormaliteService, Utilisateur } from '@europrocurement/l2d-domain';
import {
    ActionLoggerApiApiLogActionCollectionDossiersGetActionLoggerCollectionDossierClientCollectionRequest,
    ActionLoggerApiApiLogActionCollectionDossiersGetActionLoggerCollectionDossierCollectionRequest,
    ActionLoggerApiApiLogActionDevisGetActionLoggerDevisClientCollectionRequest,
    ActionLoggerApiApiLogActionDevisGetActionLoggerDevisCollectionRequest,
    ActionLoggerApiApiLogActionDossiersGetActionLoggerDossierClientCollectionRequest,
    ActionLoggerApiApiLogActionDossiersGetActionLoggerDossierCollectionRequest,
    ActionLoggerApiApiLogActionFacturesGetActionLoggerFactureCollectionRequest,
    ActionLoggerApiApiLogActionFacturesGetActionLoggerFactureClientCollectionRequest,
    ActionLoggerApiApiLogActionTiersGetActionLoggerTiersClientCollectionRequest,
    ActionLoggerApiApiLogActionTiersGetActionLoggerTiersCollectionRequest,
    LogActionCollectionDossierJsonldLogRead,
    LogActionCollectionDossierJsonldLogReadExpanded,
    LogActionDossierJsonldLogRead,
    LogActionTiersJsonldLogRead,
    LogActionTiersJsonldLogReadExpanded,
    LogActionFactureLogActionDtoJsonldLogRead,
    LogActionFactureLogActionDtoJsonldLogReadExpanded,
    LogActionDevisLogActionDtoJsonldLogRead,
    LogActionDevisLogActionDtoJsonldLogReadExpanded,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { UserJsonld } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { useCallback, useState } from 'react';
import { useUser } from '../modules/Utilisateur';
import { EMAIL_VALIDATION_REGEX } from '../validation';
import { useUtilisateur } from './useUtilisateur';

export type ActionLoggerRequest = {
    entityId: number | string;
    requestLimited: boolean; // requête pour front
    withLogsTech?: boolean; // disponible que pour requête back
    page?: number;
    itemsPerPage?: number;
};
interface UserInfoCache {
    [userKeucloak: string]: { firstName: string; lastName: string } | null;
}
const useActionLogger = () => {
    const { ActionLoggerApi } = useFormaliteService();

    // TIERS
    const getLogActionTiers = (
        entityId: number | string,
        requestLimited = true,
        withLogsTech = false,
        page = 1,
        itemsPerPage = 10,
    ): Promise<LogActionTiersJsonldLogRead[] | LogActionTiersJsonldLogReadExpanded[]> =>
        new Promise((resolve, reject) => {
            const requestParameter = {
                entityId,
                page,
                itemsPerPage,
            };

            if (requestLimited) {
                // FRONT
                ActionLoggerApi.apiLogActionTiersGetActionLoggerTiersClientCollection(
                    requestParameter as ActionLoggerApiApiLogActionTiersGetActionLoggerTiersClientCollectionRequest,
                )
                    .then((res) => {
                        resolve(res.data['hydra:member'] as LogActionTiersJsonldLogRead[]);
                    })
                    .catch((err) => {
                        reject(err);
                    });

                return;
            }

            // BACK
            ActionLoggerApi.apiLogActionTiersGetActionLoggerTiersCollection({
                ...requestParameter,
                withTech: withLogsTech,
            } as ActionLoggerApiApiLogActionTiersGetActionLoggerTiersCollectionRequest)
                .then((res) => {
                    resolve(res.data['hydra:member'] as LogActionTiersJsonldLogReadExpanded[]);
                })
                .catch((err) => {
                    reject(err);
                });
        });

    // DOSSIER
    const getLogActionDossier = (
        entityId: number | string,
        requestLimited = true,
        withLogsTech = false,
        page = 1,
        itemsPerPage = 10,
    ): Promise<LogActionDossierJsonldLogRead[]> =>
        new Promise((resolve, reject) => {
            const requestParameter = {
                entityId,
                page,
                itemsPerPage,
            };

            if (requestLimited) {
                // FRONT
                ActionLoggerApi.apiLogActionDossiersGetActionLoggerDossierClientCollection(
                    requestParameter as ActionLoggerApiApiLogActionDossiersGetActionLoggerDossierClientCollectionRequest,
                )
                    .then((res) => {
                        resolve(res.data['hydra:member'] as LogActionDossierJsonldLogRead[]);
                    })
                    .catch(() => {
                        reject();
                    });
                return;
            }

            ActionLoggerApi.apiLogActionDossiersGetActionLoggerDossierCollection({
                ...requestParameter,
                withTech: withLogsTech,
            } as ActionLoggerApiApiLogActionDossiersGetActionLoggerDossierCollectionRequest)
                .then((res) => {
                    resolve(res.data['hydra:member'] as LogActionDossierJsonldLogRead[]);
                })
                .catch(() => {
                    reject();
                });
        });

    // COLLECTION
    const getLogActionCollectionDossier = (
        entityId: number | string,
        requestLimited = true,
        withLogsTech = false,
        page = 1,
        itemsPerPage = 10,
    ): Promise<
        | LogActionCollectionDossierJsonldLogRead[]
        | LogActionCollectionDossierJsonldLogReadExpanded[]
    > =>
        new Promise((resolve, reject) => {
            const requestParameter = {
                entityId,
                page,
                itemsPerPage,
            };

            if (requestLimited) {
                // FRONT
                ActionLoggerApi.apiLogActionCollectionDossiersGetActionLoggerCollectionDossierClientCollection(
                    requestParameter as ActionLoggerApiApiLogActionCollectionDossiersGetActionLoggerCollectionDossierClientCollectionRequest,
                )
                    .then((res) => {
                        resolve(
                            res.data['hydra:member'] as LogActionCollectionDossierJsonldLogRead[],
                        );
                    })
                    .catch((err) => {
                        reject(err);
                    });

                return;
            }

            // BACK
            ActionLoggerApi.apiLogActionCollectionDossiersGetActionLoggerCollectionDossierCollection(
                {
                    ...requestParameter,
                    withTech: withLogsTech,
                } as ActionLoggerApiApiLogActionCollectionDossiersGetActionLoggerCollectionDossierCollectionRequest,
            )
                .then((res) => {
                    resolve(
                        res.data[
                            'hydra:member'
                        ] as LogActionCollectionDossierJsonldLogReadExpanded[],
                    );
                })
                .catch((err) => {
                    reject(err);
                });
        });

    // FACTURE
    const getLogActionFacture = (
        entityId: number | string,
        requestLimited = true,
        withLogsTech = false,
        page = 1,
        itemsPerPage = 10,
    ): Promise<
        | LogActionFactureLogActionDtoJsonldLogRead[]
        | LogActionFactureLogActionDtoJsonldLogReadExpanded[]
    > =>
        new Promise((resolve, reject) => {
            const requestParameter = {
                entityId,
                page,
                itemsPerPage,
            };

            if (requestLimited) {
                // FRONT
                ActionLoggerApi.apiLogActionFacturesGetActionLoggerFactureClientCollection(
                    requestParameter as ActionLoggerApiApiLogActionFacturesGetActionLoggerFactureClientCollectionRequest,
                )
                    .then((res) => {
                        resolve(
                            res.data['hydra:member'] as LogActionFactureLogActionDtoJsonldLogRead[],
                        );
                    })
                    .catch((err) => {
                        reject(err);
                    });

                return;
            }

            // BACK
            ActionLoggerApi.apiLogActionFacturesGetActionLoggerFactureCollection({
                ...requestParameter,
                withTech: withLogsTech,
            } as ActionLoggerApiApiLogActionFacturesGetActionLoggerFactureCollectionRequest)
                .then((res) => {
                    resolve(
                        res.data[
                            'hydra:member'
                        ] as LogActionFactureLogActionDtoJsonldLogReadExpanded[],
                    );
                })
                .catch((err) => {
                    reject(err);
                });
        });

    // DEVIS
    const getLogActionDevis = (
        entityId: number | string,
        requestLimited = true,
        withLogsTech = false,
        page = 1,
        itemsPerPage = 10,
    ): Promise<
        | LogActionDevisLogActionDtoJsonldLogRead[]
        | LogActionDevisLogActionDtoJsonldLogReadExpanded[]
    > =>
        new Promise((resolve, reject) => {
            const requestParameter = {
                entityId,
                page,
                itemsPerPage,
            };

            if (requestLimited) {
                // FRONT
                ActionLoggerApi.apiLogActionDevisGetActionLoggerDevisClientCollection(
                    requestParameter as ActionLoggerApiApiLogActionDevisGetActionLoggerDevisClientCollectionRequest,
                )
                    .then((res) => {
                        resolve(
                            res.data['hydra:member'] as LogActionDevisLogActionDtoJsonldLogRead[],
                        );
                    })
                    .catch((err) => {
                        reject(err);
                    });

                return;
            }

            // BACK
            ActionLoggerApi.apiLogActionDevisGetActionLoggerDevisCollection({
                ...requestParameter,
                withTech: withLogsTech,
            } as ActionLoggerApiApiLogActionDevisGetActionLoggerDevisCollectionRequest)
                .then((res) => {
                    resolve(
                        res.data[
                            'hydra:member'
                        ] as LogActionDevisLogActionDtoJsonldLogReadExpanded[],
                    );
                })
                .catch((err) => {
                    reject(err);
                });
        });

    /**
     * Formate le nom et prénom d'un utilisateur selon les conventions d'affichage
     */
    const formatUserName = (
        prenom?: string,
        nom?: string,
    ): { firstName: string; lastName: string } => {
        if (!prenom || !nom) {
            return { firstName: '', lastName: '' };
        }

        return {
            firstName: prenom.charAt(0).toUpperCase() + prenom.slice(1).toLowerCase(),
            lastName: nom.toUpperCase(),
        };
    };

    /**
     * Résout les informations utilisateur à partir d'un userkeycloak avec mise en cache
     */
    const [userCache, setUserCache] = useState<UserInfoCache>({});
    const { fetchUser, cache } = useUser();
    const { getUserByEmail } = useUtilisateur();
    const resolveUserInfo = useCallback(
        async (userKeycloak: string): Promise<UserJsonld | Utilisateur | null> => {
            if (cache[userKeycloak] !== undefined) {
                return cache[userKeycloak];
            }

            if (userCache[userKeycloak] !== undefined) {
                return userCache[userKeycloak];
            }
            console.log(EMAIL_VALIDATION_REGEX.test(userKeycloak), 'email validation');

            if (EMAIL_VALIDATION_REGEX.test(userKeycloak) === true) {
                try {
                    const user = await getUserByEmail(userKeycloak);
                    setUserCache((prev) => ({
                        ...prev,
                        [userKeycloak]: formatUserName(user.prenom, user.nom),
                    }));
                    return user;
                } catch (error) {
                    console.warn(
                        `Impossible de récupérer l'utilisateur avec l'email: ${userKeycloak}`,
                        error,
                    );
                    setUserCache((prev) => ({ ...prev, [userKeycloak]: null }));
                    return null;
                }
            }

            try {
                const user = cache[userKeycloak] ?? (await fetchUser(userKeycloak));
                return user;
            } catch {
                return null;
            }
        },
        [userCache, cache, fetchUser, getUserByEmail],
    );

    return {
        getLogActionDossier,
        getLogActionTiers,
        getLogActionCollectionDossier,
        getLogActionFacture,
        getLogActionDevis,
        resolveUserInfo,
    };
};

export default useActionLogger;
